import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Send, Search, 
  Check, CheckCheck, MessageCircle, Plus, Users, 
  X, Minimize2, Smile, Paperclip, Mic, MoreVertical, UsersRound, UserPlus, Edit2, Crown,
  Reply, Image, FileText, Download, XCircle
} from 'lucide-react';
import { Globe, Briefcase, Handshake } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import EmojiPicker from 'emoji-picker-react';

// WhatsApp exact colors
const WA_COLORS = {
  primary: '#075E54',
  secondary: '#128C7E',
  lightGreen: '#25D366',
  outgoingBubble: '#D9FDD3',
  incomingBubble: '#FFFFFF',
  chatBg: '#EFEAE2',
  chatPattern: '#D1D7DB',
  blueTick: '#53BDEB',
  textPrimary: '#111B21',
  textSecondary: '#667781',
  panelBg: '#F0F2F5',
  divider: '#E9EDEF',
  headerBg: '#008069',
  sidebarHeader: '#008069',
  searchBg: '#F0F2F5',
  unreadBadge: '#25D366',
  selectedChat: '#F0F2F5',
};

// Helper function for Title Case
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

// Create notification sound using Web Audio API - WhatsApp-style pleasant tone
const createNotificationSound = () => {
  return {
    play: () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Create a pleasant two-tone notification (like WhatsApp)
        const playTone = (frequency, startTime, duration) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = frequency;
          oscillator.type = 'sine';
          
          // Smooth envelope
          gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
          gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + startTime + 0.02);
          gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + startTime + duration * 0.5);
          gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + startTime + duration);
          
          oscillator.start(audioContext.currentTime + startTime);
          oscillator.stop(audioContext.currentTime + startTime + duration);
        };
        
        // Two-note notification: G5 then C6 (pleasant ascending)
        playTone(784, 0, 0.15);      // G5
        playTone(1047, 0.12, 0.2);   // C6
        
      } catch (e) {
        console.log('Audio not supported');
      }
    }
  };
};

// Alternative notification sounds you can choose from
const NOTIFICATION_SOUNDS = {
  whatsapp: () => {
    // WhatsApp-style two-tone
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playNote = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + start + 0.02);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    playNote(784, 0, 0.12);
    playNote(1047, 0.1, 0.18);
  },
  
  bubble: () => {
    // Bubble pop sound
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.1);
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  },
  
  chime: () => {
    // Gentle chime
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playChime = (freq, start) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'triangle';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + 0.4);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + 0.4);
    };
    playChime(880, 0);     // A5
    playChime(1109, 0.1);  // C#6
    playChime(1319, 0.2);  // E6
  },
  
  ding: () => {
    // Simple ding
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 830;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  },
  
  ping: () => {
    // Quick ping sound
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(1200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.08);
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.12);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
  }
};

// Current sound setting - can be changed to: 'whatsapp', 'bubble', 'chime', 'ding', 'ping'
const CURRENT_SOUND = 'whatsapp';

const Ping = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  // Edit group state
  const [editGroupName, setEditGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [newMembersToAdd, setNewMembersToAdd] = useState([]);
  const [editGroupUserSearch, setEditGroupUserSearch] = useState('');
  const [savingGroup, setSavingGroup] = useState(false);
  
  // Reply state
  const [replyingTo, setReplyingTo] = useState(null);
  
  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollingRef = useRef(null);
  const presenceRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const emojiPickerRef = useRef(null);
  
  const { user, currentOffice, getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Play notification sound using Web Audio API
  const playNotificationSound = useCallback(() => {
    try {
      NOTIFICATION_SOUNDS[CURRENT_SOUND]();
    } catch (e) {
      // Fallback or silent
      console.log('Could not play notification sound');
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/chat/conversations`, { headers });
      if (response.ok) {
        const data = await response.json();
        
        // Check for new messages and play sound
        const newTotalMessages = data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        if (newTotalMessages > lastMessageCount && lastMessageCount > 0) {
          playNotificationSound();
        }
        setLastMessageCount(newTotalMessages);
        
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, currentOffice, getAuthHeaders, lastMessageCount, playNotificationSound]);

  // Fetch messages
  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/messages`, { headers });
      if (response.ok) {
        const data = await response.json();
        
        // Check for new messages
        if (data.length > messages.length && messages.length > 0) {
          const lastMsg = data[data.length - 1];
          if (!lastMsg.is_mine) {
            playNotificationSound();
          }
        }
        
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [API_URL, getAuthHeaders, messages.length, playNotificationSound]);

  // Fetch typing status
  const fetchTypingStatus = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/chat/typing/${conversationId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTypingUsers(data.typing_users || []);
      }
    } catch (error) {
      // Silently fail
    }
  }, [API_URL, getAuthHeaders]);

  // Send typing status
  const sendTypingStatus = useCallback(async (typing) => {
    if (!selectedConversation) return;
    
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/chat/typing?conversation_id=${selectedConversation.id}&is_typing=${typing}`, {
        method: 'POST',
        headers
      });
    } catch (error) {
      // Silently fail
    }
  }, [API_URL, getAuthHeaders, selectedConversation]);

  // Handle typing
  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      sendTypingStatus(true);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTypingStatus(false);
    }, 2000);
  }, [isTyping, sendTypingStatus]);

  // Fetch available users
  const fetchAvailableUsers = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const response = await fetch(`${API_URL}/api/chat/users`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Update presence
  const updatePresence = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      await fetch(`${API_URL}/api/chat/presence`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      }).catch(() => {});
      
      const response = await fetch(`${API_URL}/api/chat/online-users`, { headers }).catch(() => null);
      if (response?.ok) {
        const data = await response.json();
        setOnlineUsers(new Set(data.online_users || []));
      }
    } catch (error) {}
  }, [API_URL, currentOffice, getAuthHeaders]);

  useEffect(() => {
    fetchConversations();
    updatePresence();
  }, [fetchConversations, updatePresence]);

  // Auto-open chat when navigated with state (e.g., from birthday splash)
  useEffect(() => {
    if (location.state?.openChatWith && !loading && conversations.length >= 0) {
      const recipient = location.state.openChatWith;
      // Clear the state so it doesn't re-trigger
      navigate('/ping', { replace: true, state: {} });
      handleStartNewChat({ id: recipient.id, first_name: recipient.first_name, last_name: recipient.last_name, user_id: recipient.id });
    }
  }, [location.state, loading]);

  useEffect(() => {
    pollingRef.current = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.id);
        fetchTypingStatus(selectedConversation.id);
      }
    }, 3000);

    presenceRef.current = setInterval(updatePresence, 30000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (presenceRef.current) clearInterval(presenceRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [selectedConversation, fetchConversations, fetchMessages, fetchTypingStatus, updatePresence]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isUserOnline = (userId) => onlineUsers.has(userId);

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setIsMinimized(false);
    setTypingUsers([]);
    await fetchMessages(conversation.id);
    setTimeout(() => messageInputRef.current?.focus(), 100);
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConversation || sendingMessage) return;

    // Stop typing indicator
    setIsTyping(false);
    sendTypingStatus(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    setSendingMessage(true);
    const messageContent = newMessage.trim();
    setNewMessage('');
    setShowEmojiPicker(false);
    
    // Capture reply info before clearing
    const currentReplyingTo = replyingTo;
    setReplyingTo(null);
    
    // Capture attachments before clearing
    const currentAttachments = [...attachments];
    setAttachments([]);

    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: selectedConversation.id,
      sender_id: user?.id,
      sender_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
      content: messageContent,
      message_type: currentAttachments.length > 0 ? 'file' : 'text',
      status: 'sent',
      created_at: new Date().toISOString(),
      is_mine: true,
      reply_to: currentReplyingTo ? {
        message_id: currentReplyingTo.id,
        sender_name: currentReplyingTo.sender_name,
        content: currentReplyingTo.content?.substring(0, 100),
        message_type: currentReplyingTo.message_type || 'text'
      } : null,
      attachments: currentAttachments
    };
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const response = await fetch(`${API_URL}/api/chat/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          conversation_id: selectedConversation.id,
          content: messageContent,
          message_type: currentAttachments.length > 0 ? 'file' : 'text',
          reply_to_id: currentReplyingTo?.id || null,
          attachments: currentAttachments.map(att => ({
            filename: att.filename,
            file_type: att.file_type,
            file_size: att.file_size,
            url: att.url,
            thumbnail_url: att.thumbnail_url
          }))
        })
      });

      if (response.ok) {
        await fetchMessages(selectedConversation.id);
        await fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingAttachment(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const headers = { ...getAuthHeaders() };
        delete headers['Content-Type']; // Let browser set multipart boundary
        
        const response = await fetch(`${API_URL}/api/chat/upload`, {
          method: 'POST',
          headers,
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setAttachments(prev => [...prev, data]);
        } else {
          console.error('Failed to upload file');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    
    setUploadingAttachment(false);
    e.target.value = ''; // Reset file input
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Handle emoji select
  const handleEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    messageInputRef.current?.focus();
  };

  // Handle reply to message
  const handleReplyTo = (message) => {
    setReplyingTo(message);
    messageInputRef.current?.focus();
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStartNewChat = async (recipient) => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const response = await fetch(`${API_URL}/api/chat/conversations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ recipient_id: recipient.id })
      });

      if (response.ok) {
        const newConversation = await response.json();
        await fetchConversations();
        setSelectedConversation(newConversation);
        setShowNewChatModal(false);
        await fetchMessages(newConversation.id);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedGroupMembers.length === 0) return;
    
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const response = await fetch(`${API_URL}/api/chat/groups`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: groupName.trim(),
          participant_ids: selectedGroupMembers
        })
      });

      if (response.ok) {
        const newGroup = await response.json();
        await fetchConversations();
        setSelectedConversation(newGroup);
        setShowGroupModal(false);
        setGroupName('');
        setSelectedGroupMembers([]);
        await fetchMessages(newGroup.id);
      }
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  // Fetch group members for editing
  const fetchGroupMembers = async (conversationId) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/chat/groups/${conversationId}/members`, { headers });
      if (response.ok) {
        const data = await response.json();
        setGroupMembers(data.members || []);
        setEditGroupName(data.group_name || '');
      }
    } catch (error) {
      console.error('Error fetching group members:', error);
    }
  };

  // Open edit group modal
  const handleOpenEditGroup = async () => {
    if (!selectedConversation?.is_group) return;
    await fetchAvailableUsers();
    await fetchGroupMembers(selectedConversation.id);
    setNewMembersToAdd([]);
    setEditGroupUserSearch('');
    setShowEditGroupModal(true);
  };

  // Toggle member to add
  const toggleNewMember = (userId) => {
    setNewMembersToAdd(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Save group edits
  const handleSaveGroupEdit = async () => {
    if (!selectedConversation?.id) return;
    
    setSavingGroup(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;

      const updateData = {};
      
      // Only include name if changed
      if (editGroupName.trim() && editGroupName.trim() !== selectedConversation.group_name) {
        updateData.name = editGroupName.trim();
      }
      
      // Include new members if any selected
      if (newMembersToAdd.length > 0) {
        updateData.add_participant_ids = newMembersToAdd;
      }
      
      // Only call API if there's something to update
      if (Object.keys(updateData).length > 0) {
        const response = await fetch(`${API_URL}/api/chat/groups/${selectedConversation.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          const updatedGroup = await response.json();
          // Update selected conversation with new data
          setSelectedConversation(updatedGroup);
          await fetchConversations();
        }
      }
      
      setShowEditGroupModal(false);
      setNewMembersToAdd([]);
    } catch (error) {
      console.error('Error updating group:', error);
    } finally {
      setSavingGroup(false);
    }
  };

  // Get users available to add (not already in group)
  const getAvailableUsersToAdd = () => {
    const currentMemberIds = groupMembers.map(m => m.id);
    return availableUsers.filter(u => !currentMemberIds.includes(u.id));
  };

  const toggleGroupMember = (userId) => {
    setSelectedGroupMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleClose = () => navigate(-1);
  const handleMinimize = () => setIsMinimized(true);

  // Get timezone for formatting - prioritize conversation's office timezone
  const getConversationTimezone = (conversation = selectedConversation) => {
    // Use the conversation's specific office timezone if available
    if (conversation?.office_timezone) {
      return conversation.office_timezone;
    }
    // Fallback to current office timezone
    return currentOffice?.settings?.timezone || currentOffice?.timezone || 'Africa/Dar_es_Salaam';
  };

  // Format time using conversation's office timezone
  const formatTime = (dateStr, conversation = selectedConversation) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const timezone = getConversationTimezone(conversation);
    try {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false,
        timeZone: timezone
      });
    } catch (e) {
      // Fallback if timezone is invalid
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
  };

  const formatMessageDate = (dateStr, conversation = selectedConversation) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const timezone = getConversationTimezone(conversation);
    
    // Get today and yesterday in office timezone
    const now = new Date();
    const todayInTz = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const yesterdayInTz = new Date(todayInTz);
    yesterdayInTz.setDate(yesterdayInTz.getDate() - 1);
    
    const dateInTz = new Date(date.toLocaleString('en-US', { timeZone: timezone }));

    if (dateInTz.toDateString() === todayInTz.toDateString()) return 'TODAY';
    if (dateInTz.toDateString() === yesterdayInTz.toDateString()) return 'YESTERDAY';
    
    try {
      return date.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric',
        timeZone: timezone
      }).toUpperCase();
    } catch (e) {
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
    }
  };

  // Format time for conversation list (uses each conversation's own timezone)
  const formatConversationTime = (dateStr, conversation) => {
    return formatTime(dateStr, conversation);
  };

  const filteredConversations = conversations.filter(c =>
    (c.is_group ? c.group_name : c.other_participant_name)?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = availableUsers.filter(u =>
    u.name?.toLowerCase().includes(userSearchQuery.toLowerCase())
  );
  
  // Separate same-office and cross-office users
  const sameOfficeUsers = filteredUsers.filter(u => !u.is_cross_office);
  const crossOfficeUsers = filteredUsers.filter(u => u.is_cross_office);

  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatMessageDate(message.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  // Get conversation display name
  const getConversationName = (conv) => {
    if (conv.is_group) {
      return toTitleCase(conv.group_name);
    }
    return toTitleCase(conv.other_participant_name) || 'Unknown';
  };

  // Get conversation avatar initials
  const getConversationInitials = (conv) => {
    if (conv.is_group) {
      return conv.group_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'G';
    }
    return conv.other_participant_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
  };

  // Minimized floating button
  if (isMinimized) {
    return (
      <motion.div 
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="fixed bottom-6 right-6 z-50 cursor-pointer"
        onClick={() => setIsMinimized(false)}
        data-testid="ping-minimized"
      >
        <motion.div 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="relative"
        >
          <div 
            className="w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-xl"
            style={{ backgroundColor: WA_COLORS.lightGreen }}
          >
            <MessageCircle className="w-7 h-7 text-white" fill="white" />
          </div>
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] text-white text-xs rounded-full flex items-center justify-center font-medium px-1 bg-red-500">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </motion.div>
      </motion.div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleClose}
        data-testid="ping-backdrop"
      />
      
      {/* WhatsApp Web Style Window */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 350 }}
        className="fixed inset-x-8 inset-y-16 z-50 flex rounded-lg overflow-hidden"
        style={{ 
          boxShadow: '0 2px 10px 0 rgba(0,0,0,.2)',
          maxWidth: '1200px',
          margin: '0 auto',
        }}
        data-testid="ping-popup"
      >
        {/* Left Panel - Conversations List */}
        <div className="w-[380px] flex flex-col border-r" style={{ backgroundColor: '#FFFFFF', borderColor: WA_COLORS.divider }}>
          {/* Left Header */}
          <div 
            className="flex items-center justify-between px-4 py-3"
            style={{ backgroundColor: WA_COLORS.headerBg }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setShowGroupModal(true); fetchAvailableUsers(); }}
                className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                title="Create Group"
                data-testid="new-group-button"
              >
                <UsersRound className="h-5 w-5" />
              </button>
              <button
                onClick={() => { setShowNewChatModal(true); fetchAvailableUsers(); }}
                className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                data-testid="new-ping-button"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={handleMinimize}
                className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                data-testid="minimize-ping-button"
              >
                <Minimize2 className="h-5 w-5" />
              </button>
              <button
                onClick={handleClose}
                className="text-white hover:bg-white/10 p-2 rounded-full transition-colors"
                data-testid="close-ping-button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-3 py-2" style={{ backgroundColor: WA_COLORS.panelBg }}>
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white">
              <Search className="h-4 w-4" style={{ color: WA_COLORS.textSecondary }} />
              <input
                type="text"
                placeholder="Search or start new chat"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[14px]"
                style={{ color: WA_COLORS.textPrimary }}
                data-testid="search-conversations"
              />
            </div>
          </div>

          {/* Conversations */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200" style={{ borderTopColor: WA_COLORS.headerBg }}></div>
              </div>
            ) : filteredConversations.length > 0 ? (
              <div>
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv)}
                    className="flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors"
                    style={{ 
                      backgroundColor: selectedConversation?.id === conv.id ? WA_COLORS.selectedChat : 'transparent',
                    }}
                    onMouseEnter={(e) => { if (selectedConversation?.id !== conv.id) e.currentTarget.style.backgroundColor = '#F5F6F6'; }}
                    onMouseLeave={(e) => { if (selectedConversation?.id !== conv.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="relative flex-shrink-0">
                      {conv.other_participant_photo && !conv.is_group ? (
                        <img 
                          src={conv.other_participant_photo.startsWith('data:') || conv.other_participant_photo.startsWith('http') 
                            ? conv.other_participant_photo 
                            : `${API_URL}${conv.other_participant_photo}`}
                          alt={conv.other_participant_name || 'User'}
                          className="w-[49px] h-[49px] rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-[49px] h-[49px] rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: conv.is_group ? WA_COLORS.headerBg : '#DFE5E7',
                          display: conv.other_participant_photo && !conv.is_group ? 'none' : 'flex'
                        }}
                      >
                        {conv.is_group ? (
                          <Users className="w-6 h-6 text-white" />
                        ) : (
                          <span className="text-[18px] text-white">
                            {getConversationInitials(conv)}
                          </span>
                        )}
                      </div>
                      {!conv.is_group && isUserOnline(conv.other_participant_id) && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: WA_COLORS.lightGreen }}></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 border-b py-2" style={{ borderColor: WA_COLORS.divider }}>
                      <div className="flex items-center justify-between">
                        <h3 className="font-normal truncate text-[17px] flex items-center gap-1.5" style={{ color: WA_COLORS.textPrimary }}>
                          {getConversationName(conv)}
                          {conv.is_cross_office && (
                            <Globe className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                          )}
                        </h3>
                        <span className="text-[12px] whitespace-nowrap ml-2" style={{ color: conv.unread_count > 0 ? WA_COLORS.lightGreen : WA_COLORS.textSecondary }}>
                          {conv.last_message_time ? formatConversationTime(conv.last_message_time, conv) : ''}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <p className="text-[14px] truncate" style={{ color: WA_COLORS.textSecondary }}>
                          {conv.is_group && conv.last_message_sender && (
                            <span className="font-medium">{toTitleCase(conv.last_message_sender.split(' ')[0])}: </span>
                          )}
                          {conv.last_message || 'Start a conversation...'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="ml-2 min-w-[20px] h-[20px] text-white text-[11px] rounded-full flex items-center justify-center font-medium" style={{ backgroundColor: WA_COLORS.lightGreen }}>
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 p-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: WA_COLORS.panelBg }}>
                  <MessageCircle className="h-8 w-8" style={{ color: WA_COLORS.textSecondary }} />
                </div>
                <p className="text-[14px]" style={{ color: WA_COLORS.textSecondary }}>No conversations yet</p>
                <button
                  onClick={() => { setShowNewChatModal(true); fetchAvailableUsers(); }}
                  className="mt-4 px-4 py-2 rounded-full text-white text-[14px]"
                  style={{ backgroundColor: WA_COLORS.headerBg }}
                >
                  Start a chat
                </button>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right Panel - Chat Area */}
        <div className="flex-1 flex flex-col" style={{ backgroundColor: WA_COLORS.chatBg }}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: WA_COLORS.panelBg, borderBottom: `1px solid ${WA_COLORS.divider}` }}>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {selectedConversation.other_participant_photo && !selectedConversation.is_group ? (
                      <img 
                        src={selectedConversation.other_participant_photo.startsWith('data:') || selectedConversation.other_participant_photo.startsWith('http') 
                          ? selectedConversation.other_participant_photo 
                          : `${API_URL}${selectedConversation.other_participant_photo}`}
                        alt={selectedConversation.other_participant_name || 'User'}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center" 
                      style={{ 
                        backgroundColor: selectedConversation.is_group ? WA_COLORS.headerBg : '#DFE5E7',
                        display: selectedConversation.other_participant_photo && !selectedConversation.is_group ? 'none' : 'flex'
                      }}
                    >
                      {selectedConversation.is_group ? (
                        <Users className="w-5 h-5 text-white" />
                      ) : (
                        <span style={{ color: '#FFFFFF' }}>
                          {getConversationInitials(selectedConversation)}
                        </span>
                      )}
                    </div>
                    {!selectedConversation.is_group && isUserOnline(selectedConversation.other_participant_id) && (
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2" style={{ backgroundColor: WA_COLORS.lightGreen, borderColor: WA_COLORS.panelBg }}></div>
                    )}
                  </div>
                  <div 
                    className={selectedConversation.is_group ? "cursor-pointer hover:opacity-80" : ""}
                    onClick={() => selectedConversation.is_group && handleOpenEditGroup()}
                  >
                    <h3 className="font-normal text-[16px] flex items-center gap-1.5" style={{ color: WA_COLORS.textPrimary }}>
                      {getConversationName(selectedConversation)}
                      {selectedConversation.is_cross_office && (
                        <Globe className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                      )}
                    </h3>
                    <p className="text-[13px]" style={{ color: WA_COLORS.textSecondary }}>
                      {typingUsers.length > 0 ? (
                        <span className="text-green-600 italic">
                          {typingUsers.map(u => toTitleCase(u.user_name?.split(' ')[0])).join(', ')} typing...
                        </span>
                      ) : selectedConversation.is_group ? (
                        <span className="flex items-center gap-1">
                          {selectedConversation.participants?.length || 0} participants
                          <Edit2 className="w-3 h-3 inline ml-1" />
                        </span>
                      ) : (
                        isUserOnline(selectedConversation.other_participant_id) ? 'online' : 'offline'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedConversation.is_group && (
                    <button 
                      onClick={handleOpenEditGroup}
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                      title="Edit Group"
                      data-testid="edit-group-button"
                    >
                      <UserPlus className="h-5 w-5" style={{ color: WA_COLORS.textSecondary }} />
                    </button>
                  )}
                  <button className="p-2 rounded-full hover:bg-gray-200 transition-colors">
                    <Search className="h-5 w-5" style={{ color: WA_COLORS.textSecondary }} />
                  </button>
                  <button className="p-2 rounded-full hover:bg-gray-200 transition-colors">
                    <MoreVertical className="h-5 w-5" style={{ color: WA_COLORS.textSecondary }} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div 
                className="flex-1 overflow-y-auto px-16 py-4"
                style={{ 
                  backgroundColor: WA_COLORS.chatBg,
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='412' height='412' viewBox='0 0 412 412' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M206 0c113.797 0 206 92.203 206 206S319.797 412 206 412 0 319.797 0 206 92.203 0 206 0zm0 18c-103.899 0-188 84.101-188 188s84.101 188 188 188 188-84.101 188-188S309.899 18 206 18z' fill='%23D4D4D4' fill-opacity='.08'/%3E%3C/svg%3E")`,
                }}
              >
                <div className="max-w-3xl mx-auto space-y-1">
                  {Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date}>
                      <div className="flex justify-center my-4">
                        <span className="px-3 py-1 rounded-lg text-[12.5px] shadow-sm" style={{ backgroundColor: 'rgba(255,255,255,0.95)', color: WA_COLORS.textSecondary }}>
                          {date}
                        </span>
                      </div>
                      {msgs.map((message) => (
                        <motion.div
                          key={message.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${message.is_mine ? 'justify-end' : 'justify-start'} mb-1 group`}
                        >
                          <div
                            className={`max-w-[65%] rounded-lg px-3 py-1.5 relative ${message.is_mine ? 'rounded-tr-none' : 'rounded-tl-none'}`}
                            style={{ 
                              backgroundColor: message.is_mine ? WA_COLORS.outgoingBubble : WA_COLORS.incomingBubble,
                              boxShadow: '0 1px 0.5px rgba(11,20,26,.13)'
                            }}
                          >
                            <div 
                              className={`absolute top-0 w-3 h-3 ${message.is_mine ? '-right-1.5' : '-left-1.5'}`}
                              style={{
                                backgroundColor: message.is_mine ? WA_COLORS.outgoingBubble : WA_COLORS.incomingBubble,
                                clipPath: message.is_mine ? 'polygon(0 0, 100% 0, 0 100%)' : 'polygon(100% 0, 0 0, 100% 100%)'
                              }}
                            />
                            
                            {/* Reply button - shows on hover */}
                            <button
                              onClick={() => handleReplyTo(message)}
                              className={`absolute top-1 ${message.is_mine ? 'left-1' : 'right-1'} opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-black/10`}
                              title="Reply"
                            >
                              <Reply className="h-4 w-4" style={{ color: WA_COLORS.textSecondary }} />
                            </button>
                            
                            {/* Show sender name in group chats */}
                            {selectedConversation.is_group && !message.is_mine && (
                              <p className="text-[12px] font-medium mb-0.5" style={{ color: WA_COLORS.headerBg }}>
                                {toTitleCase(message.sender_name)}
                              </p>
                            )}
                            
                            {/* Reply preview if replying to a message */}
                            {message.reply_to && (
                              <div 
                                className="mb-1 p-2 rounded-md border-l-4 bg-black/5"
                                style={{ borderLeftColor: WA_COLORS.headerBg }}
                              >
                                <p className="text-[11px] font-medium" style={{ color: WA_COLORS.headerBg }}>
                                  {message.reply_to.sender_name}
                                </p>
                                <p className="text-[12px] truncate" style={{ color: WA_COLORS.textSecondary }}>
                                  {message.reply_to.content}
                                </p>
                              </div>
                            )}
                            
                            {/* Attachments */}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mb-1 space-y-1">
                                {message.attachments.map((att, idx) => (
                                  <div key={idx} className="rounded overflow-hidden">
                                    {att.file_type?.startsWith('image/') ? (
                                      <a href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer">
                                        <img 
                                          src={`${API_URL}${att.url}`} 
                                          alt={att.filename}
                                          className="max-w-full max-h-48 rounded"
                                        />
                                      </a>
                                    ) : (
                                      <a 
                                        href={`${API_URL}${att.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 p-2 bg-black/5 rounded hover:bg-black/10"
                                      >
                                        <FileText className="h-8 w-8" style={{ color: WA_COLORS.headerBg }} />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm truncate">{att.filename}</p>
                                          <p className="text-xs" style={{ color: WA_COLORS.textSecondary }}>
                                            {(att.file_size / 1024).toFixed(1)} KB
                                          </p>
                                        </div>
                                        <Download className="h-4 w-4" style={{ color: WA_COLORS.textSecondary }} />
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <p className="text-[14.2px] whitespace-pre-wrap break-words" style={{ color: WA_COLORS.textPrimary }}>
                              {message.content}
                              <span className="inline-flex items-center gap-1 ml-2 float-right translate-y-1">
                                <span className="text-[11px]" style={{ color: WA_COLORS.textSecondary }}>{formatTime(message.created_at)}</span>
                                {message.is_mine && (
                                  <CheckCheck className="h-[16px] w-[16px]" style={{ color: message.status === 'read' ? WA_COLORS.blueTick : WA_COLORS.textSecondary }} />
                                )}
                              </span>
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ))}
                  
                  {/* Typing indicator */}
                  {typingUsers.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start mb-1"
                    >
                      <div
                        className="rounded-lg px-4 py-2 rounded-tl-none"
                        style={{ backgroundColor: WA_COLORS.incomingBubble, boxShadow: '0 1px 0.5px rgba(11,20,26,.13)' }}
                      >
                        <div className="flex items-center gap-1">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input Area */}
              <div style={{ backgroundColor: WA_COLORS.panelBg }}>
                {/* Reply Preview */}
                <AnimatePresence>
                  {replyingTo && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 pt-2"
                    >
                      <div 
                        className="flex items-center gap-2 p-2 rounded-lg border-l-4"
                        style={{ 
                          backgroundColor: WA_COLORS.incomingBubble, 
                          borderLeftColor: WA_COLORS.headerBg 
                        }}
                      >
                        <Reply className="h-4 w-4" style={{ color: WA_COLORS.headerBg }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium" style={{ color: WA_COLORS.headerBg }}>
                            Replying to {replyingTo.sender_name}
                          </p>
                          <p className="text-sm truncate" style={{ color: WA_COLORS.textSecondary }}>
                            {replyingTo.content?.substring(0, 50)}...
                          </p>
                        </div>
                        <button onClick={cancelReply} className="p-1 hover:bg-black/10 rounded">
                          <X className="h-4 w-4" style={{ color: WA_COLORS.textSecondary }} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Attachments Preview */}
                <AnimatePresence>
                  {attachments.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 pt-2"
                    >
                      <div className="flex gap-2 flex-wrap">
                        {attachments.map((att, idx) => (
                          <div key={idx} className="relative group">
                            {att.file_type?.startsWith('image/') ? (
                              <img 
                                src={`${API_URL}${att.url}`} 
                                alt={att.filename}
                                className="h-16 w-16 object-cover rounded"
                              />
                            ) : (
                              <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                                <FileText className="h-6 w-6" style={{ color: WA_COLORS.textSecondary }} />
                              </div>
                            )}
                            <button
                              onClick={() => removeAttachment(idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <div className="px-4 py-3 flex items-center gap-3">
                  {/* Emoji Picker */}
                  <div className="relative" ref={emojiPickerRef}>
                    <button 
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      <Smile className="h-6 w-6" style={{ color: WA_COLORS.textSecondary }} />
                    </button>
                    <AnimatePresence>
                      {showEmojiPicker && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: 10 }}
                          className="absolute bottom-14 left-0 z-50"
                        >
                          <EmojiPicker 
                            onEmojiClick={handleEmojiClick}
                            width={320}
                            height={400}
                            searchDisabled={false}
                            skinTonesDisabled
                            previewConfig={{ showPreview: false }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  {/* Attachment Button */}
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {uploadingAttachment ? (
                      <div className="h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Paperclip className="h-6 w-6" style={{ color: WA_COLORS.textSecondary }} />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  <form onSubmit={handleSendMessage} className="flex-1 flex items-center gap-3">
                    <input
                      ref={messageInputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message"
                      className="flex-1 px-4 py-3 rounded-lg outline-none text-[15px]"
                      style={{ backgroundColor: '#FFFFFF', color: WA_COLORS.textPrimary }}
                      data-testid="message-input"
                    />
                    <button
                      type={(newMessage.trim() || attachments.length > 0) ? 'submit' : 'button'}
                      disabled={(newMessage.trim() || attachments.length > 0) ? sendingMessage : false}
                      className="w-[52px] h-[52px] rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: WA_COLORS.headerBg }}
                      data-testid="send-message-button"
                    >
                      {(newMessage.trim() || attachments.length > 0) ? <Send className="h-5 w-5 text-white" /> : <Mic className="h-6 w-6 text-white" />}
                    </button>
                  </form>
                </div>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="flex-1 flex flex-col items-center justify-center" style={{ backgroundColor: WA_COLORS.panelBg }}>
              <div className="text-center max-w-md">
                <div className="w-[320px] h-[188px] mx-auto mb-8">
                  <svg viewBox="0 0 303 172" xmlns="http://www.w3.org/2000/svg">
                    <path fill={WA_COLORS.headerBg} fillOpacity="0.15" d="M229.565 160.229c32.647-16.02 51.189-35.174 54.126-59.108C286.628 77.188 261.2 53.02 219.527 41.73c-41.673-11.29-97.394-9.75-143.876 7.498C29.169 67.96 4.333 95.26.67 122.568c-3.662 27.308 10.953 52.217 41.838 65.763C73.39 202.1 126.354 209.25 174.92 195.39c35.12-10.03 52.666-23.106 54.646-35.161z"/>
                    <path fill={WA_COLORS.headerBg} fillOpacity="0.3" d="M151.5 0C68.016 0 0 47.453 0 106c0 21.006 8.295 40.473 22.459 56.676L7.558 188.1c-1.967 3.35.387 7.62 4.272 7.62h139.67c83.484 0 151.5-47.453 151.5-106S234.984 0 151.5 0z"/>
                  </svg>
                </div>
                <h1 className="text-[32px] font-light mb-3" style={{ color: WA_COLORS.textPrimary }}>Ping</h1>
                <p className="text-[20px] font-light mb-4" style={{ color: WA_COLORS.textSecondary }}>
                  Talk fast, work faster.
                </p>
                <p className="text-[14px] leading-relaxed" style={{ color: WA_COLORS.textSecondary }}>
                  Send and receive messages with your colleagues.<br />
                  Select a conversation or start a new chat.
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-[13px]" style={{ color: WA_COLORS.textSecondary }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M15.75 4.5L6.75 13.5L2.25 9" stroke={WA_COLORS.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  End-to-end encrypted
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* New Chat Modal */}
      <Dialog open={showNewChatModal} onOpenChange={setShowNewChatModal}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <div style={{ backgroundColor: WA_COLORS.headerBg }} className="px-4 py-4">
            <DialogTitle className="text-white text-[19px] font-normal flex items-center gap-3">
              <button onClick={() => setShowNewChatModal(false)} className="hover:bg-white/10 p-1 rounded-full">
                <X className="h-5 w-5" />
              </button>
              New chat
            </DialogTitle>
          </div>
          
          <div style={{ backgroundColor: WA_COLORS.panelBg }} className="px-3 py-2">
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white">
              <Search className="h-4 w-4" style={{ color: WA_COLORS.textSecondary }} />
              <input
                type="text"
                placeholder="Search name or number"
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[14px]"
                style={{ color: WA_COLORS.textPrimary }}
                data-testid="search-users"
              />
            </div>
          </div>
          
          <ScrollArea className="h-80 bg-white">
            {filteredUsers.length > 0 ? (
              <div>
                {/* Same-office users */}
                {sameOfficeUsers.map((chatUser) => (
                  <div
                    key={chatUser.id}
                    onClick={() => handleStartNewChat(chatUser)}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F5F6F6] transition-colors"
                    data-testid={`user-${chatUser.id}`}
                  >
                    <div className="relative">
                      {chatUser.profile_picture ? (
                        <img 
                          src={chatUser.profile_picture.startsWith('data:') || chatUser.profile_picture.startsWith('http') 
                            ? chatUser.profile_picture 
                            : `${API_URL}${chatUser.profile_picture}`}
                          alt={chatUser.name || 'User'}
                          className="w-[49px] h-[49px] rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-[49px] h-[49px] rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: '#DFE5E7',
                          display: chatUser.profile_picture ? 'none' : 'flex'
                        }}
                      >
                        <span className="text-[18px] text-white">{chatUser.initials}</span>
                      </div>
                      {chatUser.badge_type ? (
                        <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white ${chatUser.badge_type === 'service_provider' ? 'bg-emerald-500' : 'bg-violet-500'}`} title={chatUser.badge_label}>
                          {chatUser.badge_type === 'service_provider' 
                            ? <Briefcase className="h-2.5 w-2.5 text-white" /> 
                            : <Handshake className="h-2.5 w-2.5 text-white" />
                          }
                        </div>
                      ) : isUserOnline(chatUser.id) ? (
                        <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: WA_COLORS.lightGreen }}></div>
                      ) : null}
                    </div>
                    <div className="flex-1 border-b py-2" style={{ borderColor: WA_COLORS.divider }}>
                      <h4 className="text-[17px]" style={{ color: WA_COLORS.textPrimary }}>{toTitleCase(chatUser.name)}</h4>
                      <p className="text-[14px]" style={{ color: WA_COLORS.textSecondary }}>
                        {chatUser.job_title || (isUserOnline(chatUser.id) ? 'Online' : 'Offline')}
                      </p>
                    </div>
                  </div>
                ))}
                
                {/* Cross-office users section */}
                {crossOfficeUsers.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 px-4 py-2" style={{ backgroundColor: WA_COLORS.panelBg }}>
                      <Globe className="h-4 w-4 text-emerald-600" />
                      <span className="text-[13px] font-medium text-emerald-700">Cross-Office</span>
                    </div>
                    {crossOfficeUsers.map((chatUser) => (
                      <div
                        key={chatUser.id}
                        onClick={() => handleStartNewChat(chatUser)}
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F5F6F6] transition-colors"
                        data-testid={`cross-office-user-${chatUser.id}`}
                      >
                        <div className="relative">
                          {chatUser.profile_picture ? (
                            <img 
                              src={chatUser.profile_picture.startsWith('data:') || chatUser.profile_picture.startsWith('http') 
                                ? chatUser.profile_picture 
                                : `${API_URL}${chatUser.profile_picture}`}
                              alt={chatUser.name || 'User'}
                              className="w-[49px] h-[49px] rounded-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-[49px] h-[49px] rounded-full flex items-center justify-center" 
                            style={{ 
                              backgroundColor: '#DFE5E7',
                              display: chatUser.profile_picture ? 'none' : 'flex'
                            }}
                          >
                            <span className="text-[18px] text-white">{chatUser.initials}</span>
                          </div>
                          {/* Badge indicator */}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white ${chatUser.badge_type === 'service_provider' ? 'bg-emerald-500' : 'bg-violet-500'}`}>
                            {chatUser.badge_type === 'service_provider' 
                              ? <Briefcase className="h-2.5 w-2.5 text-white" /> 
                              : <Handshake className="h-2.5 w-2.5 text-white" />
                            }
                          </div>
                        </div>
                        <div className="flex-1 border-b py-2" style={{ borderColor: WA_COLORS.divider }}>
                          <div className="flex items-center gap-2">
                            <h4 className="text-[17px]" style={{ color: WA_COLORS.textPrimary }}>{toTitleCase(chatUser.name)}</h4>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${chatUser.badge_type === 'service_provider' ? 'bg-emerald-500' : 'bg-violet-500'}`} title={chatUser.badge_label}>
                              {chatUser.badge_type === 'service_provider' 
                                ? <Briefcase className="h-2.5 w-2.5 text-white" /> 
                                : <Handshake className="h-2.5 w-2.5 text-white" />
                              }
                            </div>
                          </div>
                          <p className="text-[14px]" style={{ color: WA_COLORS.textSecondary }}>
                            {chatUser.job_title}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-4">
                <Users className="h-12 w-12 mb-3" style={{ color: WA_COLORS.textSecondary, opacity: 0.5 }} />
                <p className="text-[14px]" style={{ color: WA_COLORS.textSecondary }}>No contacts found</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Create Group Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <div style={{ backgroundColor: WA_COLORS.headerBg }} className="px-4 py-4">
            <DialogTitle className="text-white text-[19px] font-normal flex items-center gap-3">
              <button onClick={() => setShowGroupModal(false)} className="hover:bg-white/10 p-1 rounded-full">
                <X className="h-5 w-5" />
              </button>
              Create Group
            </DialogTitle>
          </div>
          
          <div className="p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Group Name</label>
              <Input
                placeholder="Enter group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="w-full"
                data-testid="group-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Add Participants ({selectedGroupMembers.length} selected)
              </label>
              <ScrollArea className="h-60 border rounded-lg">
                {availableUsers.map((chatUser) => (
                  <div
                    key={chatUser.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleGroupMember(chatUser.id)}
                  >
                    <Checkbox
                      checked={selectedGroupMembers.includes(chatUser.id)}
                      onCheckedChange={() => toggleGroupMember(chatUser.id)}
                    />
                    <div className="relative">
                      {chatUser.profile_picture ? (
                        <img 
                          src={chatUser.profile_picture.startsWith('data:') || chatUser.profile_picture.startsWith('http') 
                            ? chatUser.profile_picture 
                            : `${API_URL}${chatUser.profile_picture}`}
                          alt={chatUser.name || 'User'}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: '#DFE5E7',
                          display: chatUser.profile_picture ? 'none' : 'flex'
                        }}
                      >
                        <span className="text-white">{chatUser.initials}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[15px] font-medium">{toTitleCase(chatUser.name)}</h4>
                      <p className="text-[13px] text-gray-500">{chatUser.job_title || 'Employee'}</p>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter className="px-4 pb-4">
            <Button
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || selectedGroupMembers.length === 0}
              className="w-full"
              style={{ backgroundColor: WA_COLORS.headerBg }}
            >
              <UsersRound className="h-4 w-4 mr-2" />
              Create Group ({selectedGroupMembers.length + 1} members)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Group Modal */}
      <Dialog open={showEditGroupModal} onOpenChange={setShowEditGroupModal}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <div style={{ backgroundColor: WA_COLORS.headerBg }} className="px-4 py-4">
            <DialogTitle className="text-white text-[19px] font-normal flex items-center gap-3">
              <button onClick={() => setShowEditGroupModal(false)} className="hover:bg-white/10 p-1 rounded-full">
                <X className="h-5 w-5" />
              </button>
              Edit Group
            </DialogTitle>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Group Name */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Group Name</label>
              <Input
                placeholder="Enter group name"
                value={editGroupName}
                onChange={(e) => setEditGroupName(e.target.value)}
                className="w-full"
                data-testid="edit-group-name-input"
              />
            </div>
            
            {/* Current Members */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Current Members ({groupMembers.length})
              </label>
              <ScrollArea className="h-36 border rounded-lg">
                {groupMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 px-4 py-2 border-b last:border-b-0"
                    style={{ borderColor: WA_COLORS.divider }}
                  >
                    <div className="relative">
                      {member.profile_picture ? (
                        <img 
                          src={member.profile_picture.startsWith('data:') || member.profile_picture.startsWith('http') 
                            ? member.profile_picture 
                            : `${API_URL}${member.profile_picture}`}
                          alt={member.name || 'User'}
                          className="w-9 h-9 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-9 h-9 rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: member.is_admin ? WA_COLORS.headerBg : '#DFE5E7',
                          display: member.profile_picture ? 'none' : 'flex'
                        }}
                      >
                        <span className="text-white text-sm">{member.initials}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-[14px] font-medium">{toTitleCase(member.name)}</h4>
                        {member.is_admin && (
                          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: WA_COLORS.lightGreen, color: 'white' }}>
                            <Crown className="w-3 h-3" /> Admin
                          </span>
                        )}
                        {member.is_current_user && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">You</span>
                        )}
                      </div>
                      <p className="text-[12px] text-gray-500">{member.job_title || 'Employee'}</p>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>
            
            {/* Add New Members */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Add New Members {newMembersToAdd.length > 0 && `(${newMembersToAdd.length} selected)`}
              </label>
              
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg border" style={{ borderColor: WA_COLORS.divider }}>
                <Search className="h-4 w-4" style={{ color: WA_COLORS.textSecondary }} />
                <input
                  type="text"
                  placeholder="Search colleagues..."
                  value={editGroupUserSearch}
                  onChange={(e) => setEditGroupUserSearch(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-[14px]"
                  style={{ color: WA_COLORS.textPrimary }}
                  data-testid="edit-group-search-users"
                />
              </div>
              
              <ScrollArea className="h-40 border rounded-lg">
                {getAvailableUsersToAdd()
                  .filter(u => u.name?.toLowerCase().includes(editGroupUserSearch.toLowerCase()))
                  .map((chatUser) => (
                  <div
                    key={chatUser.id}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleNewMember(chatUser.id)}
                  >
                    <Checkbox
                      checked={newMembersToAdd.includes(chatUser.id)}
                      onCheckedChange={() => toggleNewMember(chatUser.id)}
                    />
                    <div className="relative">
                      {chatUser.profile_picture ? (
                        <img 
                          src={chatUser.profile_picture.startsWith('data:') || chatUser.profile_picture.startsWith('http') 
                            ? chatUser.profile_picture 
                            : `${API_URL}${chatUser.profile_picture}`}
                          alt={chatUser.name || 'User'}
                          className="w-9 h-9 rounded-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-9 h-9 rounded-full flex items-center justify-center" 
                        style={{ 
                          backgroundColor: '#DFE5E7',
                          display: chatUser.profile_picture ? 'none' : 'flex'
                        }}
                      >
                        <span className="text-white text-sm">{chatUser.initials}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[14px] font-medium">{toTitleCase(chatUser.name)}</h4>
                      <p className="text-[12px] text-gray-500">{chatUser.job_title || 'Employee'}</p>
                    </div>
                  </div>
                ))}
                {getAvailableUsersToAdd().filter(u => u.name?.toLowerCase().includes(editGroupUserSearch.toLowerCase())).length === 0 && (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                    <Users className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No more colleagues to add</p>
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter className="px-4 pb-4 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditGroupModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveGroupEdit}
              disabled={savingGroup || (editGroupName.trim() === selectedConversation?.group_name && newMembersToAdd.length === 0)}
              className="flex-1"
              style={{ backgroundColor: WA_COLORS.headerBg }}
              data-testid="save-group-edit-button"
            >
              {savingGroup ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Saving...
                </span>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Ping;
